if (!customElements.get('quick-order-list')) {
  customElements.define(
    'quick-order-list',
    class QuickOrderList extends BulkAdd {
      cartUpdateUnsubscriber = undefined;
      hasPendingQuantityUpdate = false;
      constructor() {
        super();
        this.isListInsideModal = this.closest('bulk-modal');

        this.stickyHeaderElement = document.querySelector('sticky-header');
        if (this.stickyHeaderElement) {
          this.stickyHeader = {
            height: this.stickyHeaderElement.offsetHeight,
            type: `${this.stickyHeaderElement.getAttribute('data-sticky-type')}`,
          };
        }

        this.totalBar = this.getTotalBar();
        if (this.totalBar) {
          this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;

          this.handleResize = this.handleResize.bind(this);
          window.addEventListener('resize', this.handleResize);
        }

        this.querySelector('form').addEventListener('submit', (event) => event.preventDefault());
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
          // skip if cart event was triggered by this section
          if (event.source === this.id) return;

          this.toggleTableLoading(true);
          await this.refresh();
          this.toggleTableLoading(false);
        });

        this.initEventListeners();
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener('resize', this.handleResize);
      }

      handleResize() {
        this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;
        this.stickyHeader.height = this.stickyHeaderElement ? this.stickyHeaderElement.offsetHeight : 0;
      }

      initEventListeners() {
        this.initPopupVariantFilters();

        this.querySelectorAll('.js-paginate .pagination__item').forEach((link) => {
          link.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopPropagation();

            const url = new URL(event.currentTarget.href);

            this.toggleTableLoading(true);
            await this.refresh(url.searchParams.get('page') || '1');
            this.scrollTop();
            this.toggleTableLoading(false);
          });
        });

        this.quickOrderListTable.addEventListener('keydown', this.handleSwitchVariantOnEnter.bind(this));

        this.initVariantEventListeners();
      }

      initVariantEventListeners() {
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]'));

        this.querySelectorAll('quantity-input').forEach((qty) => {
          const debouncedOnChange = debounce(this.onChange.bind(this), BulkAdd.ASYNC_REQUEST_DELAY, true);
          qty.addEventListener('change', (event) => {
            this.hasPendingQuantityUpdate = true;
            debouncedOnChange(event);
          });
        });

        this.querySelectorAll('.quick-order-list-remove-button').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleLoading(true);
            this.startQueue(button.dataset.index, 0);
          });
        });

        this.applyPopupVariantFilters();
      }

      initPopupVariantFilters() {
        const filters = this.querySelector('[data-variant-filters]');
        if (!filters) return;

        if (!this.popupFilterValues) {
          this.popupFilterValues = Object.fromEntries(
            Array.from(filters.querySelectorAll('[data-option-filter]'), (select) => [
              select.dataset.optionFilter,
              select.value,
            ])
          );
        }
        // Honor an explicit SKU/cart link once; subsequent cart refreshes retain the shopper's filters.
        if (!this.variantArrivalInitialized) {
          this.variantArrivalInitialized = true;
          const variantId = this.isListInsideModal ? null : new URL(window.location.href).searchParams.get('variant');
          const row = variantId && /^\d+$/.test(variantId)
            ? this.querySelector(`tr.variant-item[data-variant-id="${variantId}"]`)
            : null;
          if (row) {
            filters.querySelectorAll('[data-option-filter]').forEach((select) => {
              this.popupFilterValues[select.dataset.optionFilter] = row.getAttribute(`data-option-${select.dataset.optionFilter}`) || '';
            });
            this.arrivalVariantId = variantId;
          }
        }
        this.popupFilterPage = this.popupFilterPage || 1;
        this.popupSortValue = this.popupSortValue || 'sku-ascending';
        filters.hidden = false;

        filters.querySelectorAll('[data-option-filter]').forEach((select) => {
          select.value = this.popupFilterValues[select.dataset.optionFilter] || '';
        });
        const sortSelect = filters.querySelector('[data-variant-sort]');
        if (sortSelect) sortSelect.value = this.popupSortValue;

        if (filters.dataset.bound) return;
        filters.dataset.bound = 'true';

        filters.addEventListener('change', (event) => {
          const sortSelect = event.target.closest('[data-variant-sort]');
          if (sortSelect) {
            this.popupSortValue = sortSelect.value;
            this.popupFilterPage = 1;
            this.applyPopupVariantFilters();
            return;
          }
          const select = event.target.closest('[data-option-filter]');
          if (!select) return;
          this.popupFilterValues[select.dataset.optionFilter] = select.value;
          this.popupFilterPage = 1;
          this.applyPopupVariantFilters();
        });

        filters.querySelector('[data-filter-reset]')?.addEventListener('click', () => {
          this.popupFilterValues = {};
          this.popupFilterPage = 1;
          filters.querySelectorAll('[data-option-filter]').forEach((select) => (select.value = ''));
          this.applyPopupVariantFilters();
        });

        this.querySelector('[data-filter-pagination]').addEventListener('click', (event) => {
          const button = event.target.closest('[data-filter-page]');
          if (!button) return;
          event.preventDefault();
          event.stopPropagation();
          this.popupFilterPage = Number(button.dataset.filterPage);
          this.applyPopupVariantFilters();
          this.querySelector('[data-option-filter], [data-variant-sort]')?.focus({ preventScroll: true });
          this.scrollTop();
        });
      }

      applyPopupVariantFilters() {
        const filters = this.querySelector('[data-variant-filters]');
        if (!filters) return;

        const rows = this.sortPopupVariantRows(
          Array.from(this.quickOrderListTable.querySelectorAll('tbody > tr.variant-item'))
        );
        const activeFilters = Array.from(filters.querySelectorAll('[data-option-filter]'))
          .map((select) => [select.dataset.optionFilter, select.value])
          .filter(([, value]) => value !== '');
        const matches = rows.filter((row) =>
          activeFilters.every(([position, value]) => row.getAttribute(`data-option-${position}`) === value)
        );
        const pageSize = Number(this.dataset.filterPageSize) || 50;
        const pageCount = Math.max(1, Math.ceil(matches.length / pageSize));
        if (this.arrivalVariantId) {
          const index = matches.findIndex((row) => row.dataset.variantId === this.arrivalVariantId);
          if (index >= 0) this.popupFilterPage = Math.floor(index / pageSize) + 1;
          this.arrivalVariantId = null;
        }
        this.popupFilterPage = Math.min(Math.max(this.popupFilterPage || 1, 1), pageCount);
        const pageStart = (this.popupFilterPage - 1) * pageSize;
        const visibleRows = new Set(matches.slice(pageStart, pageStart + pageSize));

        rows.forEach((row) => {
          row.hidden = !visibleRows.has(row);
          const errorRow = row.nextElementSibling;
          if (errorRow?.classList.contains('desktop-row-error')) errorRow.hidden = row.hidden;
        });

        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]')).filter(
          (input) => !input.closest('tr.variant-item')?.hidden
        );
        const count = filters.querySelector('[data-filter-count]');
        count.textContent = count.dataset.countTemplate
          .replace('[shown]', matches.length)
          .replace('[total]', rows.length);
        const resetButton = filters.querySelector('[data-filter-reset]');
        if (resetButton) resetButton.disabled = activeFilters.length === 0;
        this.querySelector('[data-filter-empty]').hidden = matches.length !== 0;
        this.renderPopupFilterPagination(pageCount);
      }

      sortPopupVariantRows(rows) {
        const direction = this.popupSortValue === 'sku-descending' ? -1 : 1;
        this.popupSkuCollator = this.popupSkuCollator || new Intl.Collator(document.documentElement.lang || undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        const pairs = rows.map((row) => ({
          row,
          errorRow: row.nextElementSibling?.classList.contains('desktop-row-error') ? row.nextElementSibling : null,
        }));
        const sorted = pairs.slice().sort((left, right) => {
          const leftSku = (left.row.dataset.sku || '').trim();
          const rightSku = (right.row.dataset.sku || '').trim();
          if (!leftSku || !rightSku) return Number(!leftSku) - Number(!rightSku);
          return direction * (this.popupSkuCollator.compare(leftSku, rightSku) || leftSku.localeCompare(rightSku));
        });
        if (sorted.every((pair, index) => pair.row === rows[index])) return rows;

        const focusedElement = document.activeElement;
        const restoreFocus = this.quickOrderListTable.contains(focusedElement);
        const body = this.quickOrderListTable.querySelector('tbody');
        sorted.forEach(({ row, errorRow }) => {
          body.append(row);
          if (errorRow) body.append(errorRow);
        });
        if (restoreFocus) focusedElement.focus({ preventScroll: true });
        return sorted.map(({ row }) => row);
      }

      renderPopupFilterPagination(pageCount) {
        const pagination = this.querySelector('[data-filter-pagination]');
        pagination.hidden = pageCount <= 1;
        const list = pagination.querySelector('ul');
        list.replaceChildren();
        if (pageCount <= 1) return;

        const addPageButton = (page, direction = null) => {
          const item = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.filterPage = page;
          button.className = 'pagination__item link';
          if (direction) {
            button.classList.add(`pagination__item--${direction}`);
            button.setAttribute('aria-label', pagination.dataset[`${direction}Label`]);
            button.append(pagination.querySelector('[data-filter-page-icon]').content.cloneNode(true));
          } else {
            button.textContent = page;
            button.setAttribute('aria-label', pagination.dataset.pageLabel.replace('[number]', page));
            if (page === this.popupFilterPage) {
              button.classList.add('pagination__item--current');
              button.setAttribute('aria-current', 'page');
              button.disabled = true;
            }
          }
          item.append(button);
          list.append(item);
        };

        if (this.popupFilterPage > 1) addPageButton(this.popupFilterPage - 1, 'previous');
        for (let page = 1; page <= pageCount; page++) addPageButton(page);
        if (this.popupFilterPage < pageCount) addPageButton(this.popupFilterPage + 1, 'next');
      }

      get currentPage() {
        return this.querySelector('.pagination-wrapper')?.dataset?.page ?? '1';
      }

      get cartVariantsForProduct() {
        return JSON.parse(this.querySelector('[data-cart-contents]')?.innerHTML || '[]');
      }

      getCartQuantityForLine(id) {
        const cartQuantity = super.getCartQuantityForLine(id);
        if (cartQuantity > 0) return cartQuantity;

        return this.cartVariantsForProduct.includes(parseInt(id, 10)) ? 1 : 0;
      }

      onChange(event) {
        const inputValue = parseInt(event.target.value);
        this.cleanErrorMessageOnType(event);
        if (inputValue == 0) {
          event.target.setAttribute('value', inputValue);
          this.startQueue(event.target.dataset.index, inputValue);
        } else {
          this.validateQuantity(event);
        }
      }

      cleanErrorMessageOnType(event) {
        const handleKeydown = () => {
          event.target.setCustomValidity(' ');
          event.target.reportValidity();
          event.target.removeEventListener('keydown', handleKeydown);
        };

        event.target.addEventListener('keydown', handleKeydown);
      }

      validateInput(target) {
        const targetValue = parseInt(target.value);
        const targetMin = parseInt(target.dataset.min);
        const targetStep = parseInt(target.step);

        if (target.max) {
          return (
            targetValue == 0 ||
            (targetValue >= targetMin && targetValue <= parseInt(target.max) && targetValue % targetStep == 0)
          );
        } else {
          return targetValue == 0 || (targetValue >= targetMin && targetValue % targetStep == 0);
        }
      }

      get quickOrderListTable() {
        return this.querySelector('.quick-order-list__table');
      }

      getSectionsToRender() {
        return [
          {
            id: this.id,
            section: this.dataset.section,
            selector: `#${this.id}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '#shopify-section-cart-icon-bubble',
          },
          {
            id: `quick-order-list-live-region-text-${this.dataset.productId}`,
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      toggleTableLoading(enable) {
        this.quickOrderListTable.classList.toggle('quick-order-list__container--disabled', enable);
        this.toggleLoading(enable);
      }

      async refresh(pageNumber = null) {
        const url = this.dataset.url || window.location.pathname;

        return fetch(`${url}?section_id=${this.dataset.section}&page=${pageNumber || this.currentPage}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const responseQuickOrderList = html.querySelector(`#${this.id}`);

            if (!responseQuickOrderList) {
              return;
            }

            const restoreScrollPosition = this.captureScrollPosition();
            this.innerHTML = responseQuickOrderList.innerHTML;
            this.initEventListeners();
            restoreScrollPosition();
          })
          .catch((e) => {
            console.error(e);
          });
      }

      renderSections(parsedState) {
        const { items, sections } = parsedState;
        const restoreScrollPosition = this.captureScrollPosition();

        this.getSectionsToRender().forEach(({ id, selector, section }) => {
          const sectionElement = document.getElementById(id);
          if (!sectionElement) return;

          const newSection = new DOMParser().parseFromString(sections[section], 'text/html').querySelector(selector);

          if (section === this.dataset.section) {
            if (this.queue.length > 0 || this.hasPendingQuantityUpdate) return;

            const focusedElement = document.activeElement;
            let focusTarget = focusedElement?.dataset?.target;
            if (focusTarget?.includes('remove')) {
              focusTarget = focusedElement.closest('quantity-popover')?.querySelector('[data-target*="increment-"]')
                ?.dataset.target;
            }

            const total = this.getTotalBar();
            if (total) {
              total.innerHTML = newSection.querySelector('.quick-order-list__total').innerHTML;
            }

            const table = this.quickOrderListTable;
            const newTable = newSection.querySelector('.quick-order-list__table');

            // only update variants if they are from the active page
            const shouldUpdateVariants =
              this.currentPage === (newSection.querySelector('.pagination-wrapper')?.dataset.page ?? '1');
            if (newTable && shouldUpdateVariants) {
              table.innerHTML = newTable.innerHTML;
              this.initVariantEventListeners();

              const newFocusTarget = this.querySelector(`[data-target='${focusTarget}']`);
              if (newFocusTarget) {
                newFocusTarget?.focus({ preventScroll: true });
              }
            }
          } else if (section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', items.length === 0);
            sectionElement.querySelector(selector).innerHTML = newSection.innerHTML;
          } else {
            sectionElement.innerHTML = newSection.innerHTML;
          }
        });
        restoreScrollPosition();
      }

      captureScrollPosition() {
        const positions = [];
        for (let element = this.quickOrderListTable?.parentElement; element; element = element.parentElement) {
          positions.push({ element, top: element.scrollTop, left: element.scrollLeft });
        }
        const container = this.querySelector('.quick-order-list__container');
        const containerPosition = container && { top: container.scrollTop, left: container.scrollLeft };
        const viewport = { top: window.scrollY, left: window.scrollX };

        return () => {
          positions.forEach(({ element, top, left }) => {
            if (!element.isConnected) return;
            if (element.scrollTop !== top) element.scrollTop = top;
            if (element.scrollLeft !== left) element.scrollLeft = left;
          });
          const currentContainer = this.querySelector('.quick-order-list__container');
          if (containerPosition && currentContainer) {
            if (currentContainer.scrollTop !== containerPosition.top) currentContainer.scrollTop = containerPosition.top;
            if (currentContainer.scrollLeft !== containerPosition.left) currentContainer.scrollLeft = containerPosition.left;
          }
          if (window.scrollY !== viewport.top || window.scrollX !== viewport.left) {
            window.scrollTo({ top: viewport.top, left: viewport.left, behavior: 'instant' });
          }
        };
      }

      getTotalBar() {
        return this.querySelector('.quick-order-list__total');
      }

      scrollTop() {
        const { top } = this.getBoundingClientRect();

        if (this.isListInsideModal) {
          this.scrollIntoView();
        } else {
          window.scrollTo({ top: top + window.scrollY - (this.stickyHeader?.height || 0), behavior: 'instant' });
        }
      }

      handleSwitchVariantOnEnter(event) {
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;

        event.preventDefault();
        event.target.blur();

        if (!this.validateInput(event.target) || this.allInputsArray.length <= 1) return;

        const currentIndex = this.allInputsArray.indexOf(event.target);
        const offset = event.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + offset + this.allInputsArray.length) % this.allInputsArray.length;

        const nextInput = this.allInputsArray[nextIndex];
        nextInput?.focus({ preventScroll: true });
        nextInput?.select();
      }

      updateMultipleQty(items) {
        if (this.queue.length == 0) this.hasPendingQuantityUpdate = false;

        this.toggleLoading(true);
        const url = this.dataset.url || window.location.pathname;
        const linesUpdate = this.startCartLinesUpdate(items);

        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map(({ section }) => section),
          sections_url: `${url}?page=${this.currentPage}`,
        });

        this.updateMessage();
        this.setErrorMessage();

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors) {
              throw Object.assign(new Error(parsedState.errors), { code: 'INVALID' });
            }

            linesUpdate?.resolve(parsedState);
            this.renderSections(parsedState);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: this.id,
              cartData: parsedState,
            });
          })
          .catch((e) => {
            if (e.code !== 'INVALID') console.error(e);
            this.dispatchCartErrorEvent(
              e.code === 'INVALID' ? e.message : window.cartStrings.error,
              e.code || 'SERVICE_UNAVAILABLE'
            );
            linesUpdate?.reject(e);
            this.setErrorMessage(e.code === 'INVALID' ? e.message : window.cartStrings.error);
          })
          .finally(() => {
            this.queue.length === 0 && this.toggleLoading(false);
            this.setRequestStarted(false);
          });
      }

      setErrorMessage(message = null) {
        this.errorMessageTemplate =
          this.errorMessageTemplate ??
          document.getElementById(`QuickOrderListErrorTemplate-${this.dataset.productId}`).cloneNode(true);
        const errorElements = document.querySelectorAll('.quick-order-list-error');

        errorElements.forEach((errorElement) => {
          errorElement.innerHTML = '';
          if (!message) return;
          const updatedMessageElement = this.errorMessageTemplate.cloneNode(true);
          updatedMessageElement.content.querySelector('.quick-order-list-error-message').innerText = message;
          errorElement.appendChild(updatedMessageElement.content);
        });
      }

      updateMessage(quantity = null) {
        const messages = this.querySelectorAll('.quick-order-list__message-text');
        const icons = this.querySelectorAll('.quick-order-list__message-icon');

        if (quantity === null || isNaN(quantity)) {
          messages.forEach((message) => (message.innerHTML = ''));
          icons.forEach((icon) => icon.classList.add('hidden'));
          return;
        }

        const isQuantityNegative = quantity < 0;
        const absQuantity = Math.abs(quantity);

        const textTemplate = isQuantityNegative
          ? absQuantity === 1
            ? window.quickOrderListStrings.itemRemoved
            : window.quickOrderListStrings.itemsRemoved
          : quantity === 1
          ? window.quickOrderListStrings.itemAdded
          : window.quickOrderListStrings.itemsAdded;

        messages.forEach((msg) => (msg.innerHTML = textTemplate.replace('[quantity]', absQuantity)));

        if (!isQuantityNegative) {
          icons.forEach((i) => i.classList.remove('hidden'));
        }
      }

      updateError(updatedValue, id) {
        let message = '';
        if (typeof updatedValue === 'undefined') {
          message = window.cartStrings.error;
        } else {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(id, message);
      }

      updateLiveRegions(id, message) {
        const variantItemErrorDesktop = document.getElementById(`Quick-order-list-item-error-desktop-${id}`);
        if (variantItemErrorDesktop) {
          variantItemErrorDesktop.querySelector('.variant-item__error-text').innerHTML = message;
          variantItemErrorDesktop.closest('tr').classList.remove('hidden');
        }
        if (variantItemErrorMobile)
          variantItemErrorMobile.querySelector('.variant-item__error-text').innerHTML = message;

        this.querySelector('#shopping-cart-variant-item-status').setAttribute('aria-hidden', true);

        const cartStatus = document.getElementById('quick-order-list-live-region-text');
        cartStatus.setAttribute('aria-hidden', false);

        setTimeout(() => {
          cartStatus.setAttribute('aria-hidden', true);
        }, 1000);
      }

      toggleLoading(loading, target = this) {
        target.querySelector('#shopping-cart-variant-item-status').toggleAttribute('aria-hidden', !loading);
        target
          .querySelectorAll('.variant-remove-total .loading__spinner')
          ?.forEach((spinner) => spinner.classList.toggle('hidden', !loading));
      }
    }
  );
}

if (!customElements.get('quick-order-list-remove-all-button')) {
  customElements.define(
    'quick-order-list-remove-all-button',
    class QuickOrderListRemoveAllButton extends HTMLElement {
      constructor() {
        super();
        this.quickOrderList = this.closest('quick-order-list');

        this.actions = {
          confirm: 'confirm',
          remove: 'remove',
          cancel: 'cancel',
        };

        this.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.dataset.action === this.actions.confirm) {
            this.toggleConfirmation(false, true);
          } else if (this.dataset.action === this.actions.remove) {
            const items = this.quickOrderList.cartVariantsForProduct.reduce(
              (acc, variantId) => ({ ...acc, [variantId]: 0 }),
              {}
            );

            this.quickOrderList.updateMultipleQty(items);
            this.toggleConfirmation(true, false);
          } else if (this.dataset.action === this.actions.cancel) {
            this.toggleConfirmation(true, false);
          }
        });
      }

      toggleConfirmation(showConfirmation, showInfo) {
        this.quickOrderList
          .querySelector('.quick-order-list-total__confirmation')
          .classList.toggle('hidden', showConfirmation);
        this.quickOrderList.querySelector('.quick-order-list-total__info').classList.toggle('hidden', showInfo);
      }
    }
  );
}
